import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

const LS_KEY = "rm_guest_cart";
const LS_DEST = "rm_ship_dest";

function readLocalCart() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalCart(items) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
  } catch {}
}

function computeTotal(items) {
  return Math.round(items.reduce((s, i) => s + (i.unit_price || 0) * i.quantity, 0) * 100) / 100;
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [destination, setDestinationState] = useState(() => {
    try { return localStorage.getItem(LS_DEST) || "MAYOTTE"; } catch { return "MAYOTTE"; }
  });
  const [shipping, setShipping] = useState(null);
  const mergedRef = useRef(false);

  const hasBodywork = items.some((item) =>
    item.shipping_included || (item.category || "").toLowerCase() === "carrosserie"
  );

  const setDestination = useCallback((code) => {
    setDestinationState(code);
    try { localStorage.setItem(LS_DEST, code); } catch {}
  }, []);

  const isAuthed = user && user !== false && user !== null;

  useEffect(() => {
    if (hasBodywork && destination !== "REUNION") setDestination("REUNION");
  }, [hasBodywork, destination, setDestination]);

  // Load on user state change
  useEffect(() => {
    if (user === null) return; // still loading
    if (!isAuthed) {
      // Guest: hydrate from localStorage
      mergedRef.current = false;
      const local = readLocalCart();
      setItems(local);
      setTotal(computeTotal(local));
      return;
    }
    // Authed: merge guest cart -> server, then load server cart
    (async () => {
      if (!mergedRef.current) {
        mergedRef.current = true;
        const local = readLocalCart();
        if (local.length > 0) {
          try {
            for (const it of local) {
              await api.post("/cart/items", {
                product_id: it.product_id,
                designation: it.designation,
                equipementier: it.equipementier,
                reference: it.reference,
                image: it.image,
                category: it.category,
                unit_price: it.unit_price,
                quantity: it.quantity,
                ferebam: it.ferebam ?? null,
                shipping_included: it.shipping_included ?? false,
              });
            }
            localStorage.removeItem(LS_KEY);
          } catch (e) {
            // Even if merge partially fails, fall through to refresh
          }
        }
      }
      try {
        const { data } = await api.get("/cart");
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch {
        setItems([]); setTotal(0);
      }
    })();
  }, [user, isAuthed]);

  const refresh = useCallback(async () => {
    if (!isAuthed) {
      const local = readLocalCart();
      setItems(local); setTotal(computeTotal(local));
      return;
    }
    try {
      const { data } = await api.get("/cart");
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setItems([]); setTotal(0);
    }
  }, [isAuthed]);

  const add = async (product) => {
    const payload = {
      product_id: product.id,
      designation: product.designation,
      equipementier: product.equipementier,
      reference: product.reference,
      image: product.image,
      category: product.category,
      unit_price: product.final_price ?? product.prix_vente_conseille ?? 0,
      supplier_name: product.supplier_name ?? null,
      supplier_unit_price: product.supplier_unit_price ?? null,
      quantity: 1,
      ferebam: product._ferebam ?? null,
      shipping_included: product.shipping_included ?? false,
    };

    if (!isAuthed) {
      // Guest cart: localStorage
      const current = readLocalCart();
      const existing = current.find((i) => i.product_id === payload.product_id);
      let next;
      if (existing) {
        next = current.map((i) =>
          i.product_id === payload.product_id ? { ...i, quantity: i.quantity + 1 } : i
        );
      } else {
        next = [...current, payload];
      }
      writeLocalCart(next);
      setItems(next);
      setTotal(computeTotal(next));
      toast.success("Ajouté au panier");
      setDrawerOpen(true);
      return true;
    }

    try {
      const { data } = await api.post("/cart/items", payload);
      setItems(data.items); setTotal(data.total);
      toast.success("Ajouté au panier");
      setDrawerOpen(true);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur ajout panier");
      return false;
    }
  };

  const updateQty = async (productId, qty) => {
    if (!isAuthed) {
      let next = readLocalCart().map((i) =>
        i.product_id === productId ? { ...i, quantity: qty } : i
      );
      if (qty <= 0) next = next.filter((i) => i.product_id !== productId);
      writeLocalCart(next);
      setItems(next); setTotal(computeTotal(next));
      return;
    }
    const { data } = await api.patch(`/cart/items/${productId}`, null, { params: { qty } });
    setItems(data.items); setTotal(data.total);
  };

  const remove = async (productId) => {
    if (!isAuthed) {
      const next = readLocalCart().filter((i) => i.product_id !== productId);
      writeLocalCart(next);
      setItems(next); setTotal(computeTotal(next));
      return;
    }
    const { data } = await api.delete(`/cart/items/${productId}`);
    setItems(data.items); setTotal(data.total);
  };

  const clear = async () => {
    if (!isAuthed) {
      writeLocalCart([]);
      setItems([]); setTotal(0);
      return;
    }
    const { data } = await api.delete("/cart");
    setItems(data.items); setTotal(data.total);
  };

  const count = items.reduce((s, i) => s + i.quantity, 0);

  // Recompute shipping whenever items or destination change
  useEffect(() => {
    if (items.length === 0) { setShipping(null); return; }
    let cancelled = false;
    const payload = {
      items: items.map((i) => ({
        product_id: i.product_id,
        designation: i.designation,
        equipementier: i.equipementier,
        reference: i.reference,
        image: i.image,
        category: i.category,
        unit_price: i.unit_price,
        quantity: i.quantity,
        shipping_included: i.shipping_included ?? false,
      })),
      destination,
    };
    api.post("/shipping/quote", payload)
      .then((r) => { if (!cancelled) setShipping(r.data); })
      .catch(() => { if (!cancelled) setShipping(null); });
    return () => { cancelled = true; };
  }, [items, destination]);

  const grandTotal = Math.round((total + (shipping?.cost || 0)) * 100) / 100;

  return (
    <CartCtx.Provider value={{
      items, total, count, drawerOpen, setDrawerOpen, refresh, add, updateQty, remove, clear,
      destination, setDestination, shipping, grandTotal, hasBodywork,
    }}>
      {children}
    </CartCtx.Provider>
  );
}
